/**
 * Document Review Modal Component
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { FileText, Download, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export function DocumentReviewModal({
  open,
  onOpenChange,
  document,
  reviewNotes,
  setReviewNotes,
  rejectionReason,
  setRejectionReason,
  processing,
  onReview
}) {
  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-lg">
        <DialogHeader>
          <DialogTitle>Review Document</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Approve or reject this document submission.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-[#1E1E1E] rounded-lg">
            <div className="flex items-start gap-3">
              <FileText className="w-8 h-8 text-[#1ABC9C]" />
              <div className="flex-1">
                <h3 className="text-[#F2F2F2] font-medium">{document.document_type_name}</h3>
                <p className="text-sm text-[#A1A1AA]">{document.file_name}</p>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  Uploaded by {document.uploaded_by_name} on{' '}
                  {document.created_at ? format(parseISO(document.created_at), 'dd/MM/yyyy') : 'N/A'}
                </p>
                {document.expires_at && (
                  <p className="text-sm text-[#A1A1AA]">
                    Expires: {format(parseISO(document.expires_at), 'dd/MM/yyyy')}
                  </p>
                )}
                <p className="text-sm text-[#A1A1AA] mt-1">
                  Medic: {document.medic_name}
                </p>
              </div>
            </div>
            {document.file_url && (
              <a
                href={document.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-[#1ABC9C] hover:underline text-sm"
              >
                <Download className="w-4 h-4" />
                Download / View Document
              </a>
            )}
          </div>

          <div className="space-y-2">
            <Label>Review Notes (optional)</Label>
            <Textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add any notes about this review..."
              className="bg-[#1E1E1E] border-white/10"
            />
          </div>

          <div className="space-y-2">
            <Label>Rejection Reason (required if rejecting)</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why this document is being rejected..."
              className="bg-[#1E1E1E] border-white/10"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => onReview(false)}
              disabled={processing}
              variant="outline"
              className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ThumbsDown className="w-4 h-4 mr-2" />
              )}
              Reject
            </Button>
            <Button
              onClick={() => onReview(true)}
              disabled={processing}
              className="flex-1 bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Approve
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
